// eslint-disable-next-line import/named, @typescript-eslint/no-unused-vars
import { ButtonNew, Icon, Typography } from "@mg/dali/src";
import {
  type ShareLinkViewAccessResponse,
  type SsoAuthorizeResponse,
  type VerifyMfaResponse,
  type WorkosLoginResponse,
} from "@mg/schemas/src/prince/auth";
import {
  Button,
  Dialog,
  Flex,
  Grid,
  Text,
  VisuallyHidden,
  TextField,
} from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import { Link } from "@tanstack/react-router";
import cx from "classnames";
import { useEffect, useReducer, useRef, useState, type FormEvent } from "react";
import { useTranslation, Trans } from "react-i18next";

import { PasswordInput } from "./PasswordInput";

import { ReactComponent as Logo } from "../images/Logo-blue.svg";
import { forgotPasswordRoute } from "../routes/forgot-password/route";
import { MfaForm } from "../routes/login/MfaForm";
import {
  shareLinkViewAccess,
  ssoAuthorize,
  verifyMfa,
  verifyUserExists,
  workOsLogin,
} from "../services/auth";
import { errorAnalyticsPayload, useAnalytics } from "../utils/analytics";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { stashShareLinkInviteSent } from "../utils/shareLinkInviteToast";
import { login } from "../utils/slices/auth";
import {
  clearEnterpriseMfaSwitch,
  setAuthenticationDialogOpen,
} from "../utils/slices/ui";

function useVerifyUserMutation() {
  return useMutation({
    mutationKey: ["verify-user"],
    mutationFn: verifyUserExists,
  });
}

function useLoginMutation() {
  return useMutation({
    mutationKey: ["login"],
    mutationFn: workOsLogin,
  });
}

function useShareLinkViewAccessMutation() {
  return useMutation({
    mutationKey: ["share-link-view-access"],
    mutationFn: shareLinkViewAccess,
  });
}

function useMfaMutation() {
  return useMutation({
    mutationKey: ["totp"],
    mutationFn: verifyMfa,
  });
}

enum AuthStep {
  EMAIL_VERIFICATION = 1,
  LOGIN = 2,
  MFA = 3,
  // Stranded INVITED user: they signed up through a share link, never set
  // a password, then their guest session expired (or they cleared cookies,
  // or opened the link from a different device). The email-only gate finds
  // an existing user but `requiresPassword` is false because they're still
  // INVITED. Auto-granting them a fresh JWT was confusing — they'd land on
  // the ticket but be unable to actually finish account setup until they
  // dug a magic link out of an old email. We now resend the invite and
  // park them on this terminal step until they click that link.
  INVITE_RESENT = 4,
}

export function AuthenticationDialog() {
  const { authenticationDialogOpen, enterpriseMfaSwitch } = useAppSelector(
    (state) => state.ui,
  );
  const dispatch = useAppDispatch();
  const posthog = useAnalytics("AuthenticationDialog");
  const user = useAppSelector((state) => state.auth.value);
  const { t } = useTranslation("auth");

  const loginMutation = useLoginMutation();
  const shareLinkViewAccessMutation = useShareLinkViewAccessMutation();
  const verifyUserMutation = useVerifyUserMutation();
  const mfaMutation = useMfaMutation();

  const [errorMessage, setErrorMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(AuthStep.EMAIL_VERIFICATION);
  const [email, setEmail] = useState("");
  const [mfaOptions, setMfaOptions] = useState<WorkosLoginResponse>({});
  const [loginPolicy, setLoginPolicy] = useState<SsoAuthorizeResponse | null>(
    null,
  );
  const [ssoLoading, setSsoLoading] = useState(false);
  const [isPasswordVisibilityToggled, togglePasswordVisibility] = useReducer(
    (x) => !x,
    false,
  );

  const formRef = useRef<HTMLFormElement>(null);

  // Submit the email straight to the share-link gate. Two modes:
  //
  // - `mode: "grant"` (default, brand-new emails) — BE creates an INVITED
  //   user, mints a JWT, and queues an invite email. We dispatch the JWT
  //   and reload so the viewer lands directly on the shared resource.
  //
  // - `mode: "resend"` (returning INVITED user with no password set, i.e.
  //   the "stranded share-link signup" case) — we still call the same BE
  //   endpoint because it conveniently re-allow-lists this resource and
  //   re-sends the invite email. We discard the returned token and park
  //   the dialog on `INVITE_RESENT` instead. Auto-logging them in was
  //   the original UX, but it stranded them inside a session they
  //   couldn't actually convert into a real account without finding
  //   their magic-link email anyway, which led to confused users
  //   reporting "I'm logged in but nothing works".
  //
  // Real ONBOARDED users never reach here — the BE rejects them and the
  // dialog routes them into LOGIN.
  const submitEmailGate = (
    submittedEmail: string,
    startTime: number,
    enterpriseId?: string,
    ticketId?: string,
    mode: "grant" | "resend" = "grant",
  ) => {
    shareLinkViewAccessMutation.mutate(
      { email: submittedEmail },
      {
        onSuccess: (data: ShareLinkViewAccessResponse | undefined) => {
          if (!data?.token) {
            // Keep this a BARE error fragment. The email-step error <Text>
            // wraps `errorMessage` in a "Request failed with error: {msg}.
            // Please try again." template, so a self-contained sentence here
            // (with its own trailing period and its own "please try again")
            // rendered as a garbled "…: Unexpected response — please try
            // again.. Please try again." once the reveal-on-errorMessage fix
            // stopped hiding it. Match the shape of every other setErrorMessage
            // call (a bare `error.message`) so the template composes cleanly.
            setErrorMessage("unexpected response");
            return;
          }

          posthog.capture("share_link_email_submitted", {
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            email: submittedEmail,
            isNewUser: data.isNewUser,
            ticketId,
            enterpriseId,
            mode,
          });

          if (mode === "resend") {
            // Stranded INVITED user: park on the "check your email" step.
            // Don't dispatch the BE-minted JWT — they need to actually
            // click the invite link so they get routed through the
            // password-setup flow rather than landing in a half-set-up
            // session that times out again on them.
            setCurrentStep(AuthStep.INVITE_RESENT);
            return;
          }

          // Brand-new INVITED account: BE just sent the invite email.
          // Stash a single-shot key so the share-link landing surface
          // can fire the "check your inbox" toast AFTER the reload —
          // calling notify() here would be lost when the page unmounts.
          // Returning INVITED users (`isNewUser === false`) didn't get
          // a new email, so we skip the stash for them.
          if (data.isNewUser) {
            stashShareLinkInviteSent();
          }

          dispatch(login({ token: data.token }));
          window.location.reload();
        },
        onError: (error) => {
          setErrorMessage(error.message);
          posthog.capture("share_link_email_submitted_error", {
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            ...errorAnalyticsPayload(error),
            email: submittedEmail,
            ticketId,
            enterpriseId,
            mode,
          });
        },
      },
    );
  };

  const handleVerifyUser = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const startTime = performance.now();
    const formData = new FormData(event.currentTarget);
    const submittedEmail = formData.get("email") as string;

    // Clear any lingering error from a previous attempt before retrying.
    // The email step reveals `errorMessage` on its own (the share-link gate
    // can set it on an "unexpected response" 2xx WITHOUT flipping either
    // mutation into `isError`), so without this reset a stale red error —
    // e.g. "Unexpected response — please try again." — stays pinned on the
    // form across resubmissions and even survives a successful retry,
    // contradicting the fresh in-flight request. Each submit starts clean.
    setErrorMessage("");

    verifyUserMutation.mutate(
      { email: submittedEmail },
      {
        onSuccess: (data) => {
          setEmail(submittedEmail);
          posthog.capture("authentication_dialog_user_verification", {
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            email: submittedEmail,
            referredBy: user?.referredBy,
          });

          // ONBOARDED user with a real password — go to the password LOGIN
          // form. This is the existing-user safety rail: we never mint a
          // session for a real account without challenging the password.
          // For SSO-enforced members, offer SSO (and hide password).
          if (data?.userExists && data.requiresPassword) {
            posthog.capture("authentication_dialog", {
              requestDurationSeconds: (performance.now() - startTime) / 1000,
              step: "Login",
              referredBy: user?.referredBy,
              email: submittedEmail,
            });
            setCurrentStep(AuthStep.LOGIN);
            ssoAuthorize({ email: submittedEmail })
              .then((policy) => {
                if (!policy) return;
                setLoginPolicy(policy);
                if (policy.mode === "sso" && policy.redirectUrl) {
                  // Enforced: jump straight to IdP (share-link members).
                  window.location.href = policy.redirectUrl;
                }
              })
              .catch(() => {
                setLoginPolicy({ mode: "password" });
              });
            return;
          }

          // Stranded INVITED user: account exists but no password yet,
          // and they didn't arrive with a fresh-from-invite-email session
          // (we'd have a real `user.email` in that case). Resend the
          // invite and stop here — don't auto-grant a session.
          if (data?.userExists && !data.requiresPassword) {
            posthog.capture("authentication_dialog", {
              requestDurationSeconds: (performance.now() - startTime) / 1000,
              step: "InviteResent",
              referredBy: user?.referredBy,
              email: submittedEmail,
            });
            submitEmailGate(
              submittedEmail,
              startTime,
              undefined,
              undefined,
              "resend",
            );
            return;
          }

          // New email — route through the email-only gate. The BE handles
          // create + allow-list, mints a JWT, and queues the invite
          // email. We dispatch the JWT and reload onto the resource.
          submitEmailGate(
            submittedEmail,
            startTime,
            // We don't decode the guest token here — the BE already has it
            // via Authorization. PostHog props get filled in by the
            // share-link route's `share_link_ticket_viewed` event instead.
            undefined,
            undefined,
            "grant",
          );
        },
        onError: (error) => {
          setErrorMessage(error.message);
          posthog.capture("authentication_dialog_user_verification_err", {
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            ...errorAnalyticsPayload(error),
            referredBy: user?.referredBy,
            email: submittedEmail,
          });
        },
      },
    );
  };

  const handleSuccess = (data: WorkosLoginResponse, startTime: number) => {
    if (data.token) {
      posthog.capture("authentication_dialog_login", {
        requestDurationSeconds: (performance.now() - startTime) / 1000,
        email,
        referredBy: user?.referredBy,
      });

      dispatch(login({ token: data.token }));
      window.location.reload();
    } else {
      setMfaOptions(data);
      setCurrentStep(AuthStep.MFA);
      posthog.capture("authentication_dialog", {
        requestDurationSeconds: (performance.now() - startTime) / 1000,
        step: "MFA",
        referredBy: user?.referredBy,
        email,
      });
    }
  };

  const handleLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const startTime = performance.now();
    const formData = new FormData(event.target as HTMLFormElement);

    loginMutation.mutate(
      {
        email,
        password: formData.get("password") as string,
      },
      {
        onSuccess: (data) => {
          handleSuccess(data as WorkosLoginResponse, startTime);
        },
        async onError(error) {
          setErrorMessage(error.message);
          posthog.capture("authentication_dialog_login_error", {
            durationSeconds: (performance.now() - startTime) / 1000,
            ...errorAnalyticsPayload(error),
            email,
            referredBy: user?.referredBy,
          });
        },
      },
    );
  };

  const renderForm = () => {
    switch (currentStep) {
      case AuthStep.EMAIL_VERIFICATION:
        return (
          <form
            onSubmit={handleVerifyUser}
            className="mt-4 grid gap-4"
            ref={formRef}
            data-testid="auth-dialog-email-form"
            aria-label={t("authenticationDialog.emailVerification")}
          >
            <Grid gap="2">
              <Text weight="medium" htmlFor="email-input">
                {t("email.label")}
              </Text>
              <TextField.Root
                required
                variant="classic"
                name="email"
                id="email-input"
                data-testid="email-field"
                placeholder={t("email.placeholder")}
                type="email"
                autoComplete="true"
                size="2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Text
                color="red"
                size="2"
                data-testid="email-error-message"
                className={cx({
                  // Hide only when there is genuinely nothing to show. The
                  // share-link gate can set `errorMessage` on an "unexpected
                  // response" (a 2xx with no token) WITHOUT either mutation
                  // entering an error state, so gating purely on `isError`
                  // swallowed that message and left the user with a cleared
                  // spinner and no feedback. Reveal whenever `errorMessage`
                  // is populated too.
                  hidden:
                    !errorMessage &&
                    !verifyUserMutation.isError &&
                    !shareLinkViewAccessMutation.isError,
                })}
              >
                {t("email.errorPrefix")} {errorMessage}.{" "}
                {t("email.errorSuffix")}
              </Text>
            </Grid>
            <Flex>
              <Button
                type="submit"
                loading={
                  verifyUserMutation.isPending ||
                  shareLinkViewAccessMutation.isPending
                }
                disabled={
                  verifyUserMutation.isPending ||
                  shareLinkViewAccessMutation.isPending
                }
                className="mt-4 w-full"
              >
                {t("buttons.continue")}
              </Button>
            </Flex>
          </form>
        );
      case AuthStep.LOGIN: {
        const showPassword =
          loginPolicy == null ||
          loginPolicy.mode === "password" ||
          loginPolicy.mode === "mg" ||
          loginPolicy.mode === "sso_optional";
        const showSso =
          loginPolicy?.mode === "sso" || loginPolicy?.mode === "sso_optional";
        return (
          <form
            onSubmit={handleLogin}
            className="mt-4 grid gap-4"
            data-testid="auth-dialog-login-form"
            aria-label={t("authenticationDialog.login")}
          >
            {showPassword ? (
              <Grid gap="2">
                <Text weight="medium" htmlFor="password">
                  {t("password.label")}
                </Text>
                <PasswordInput
                  type="password"
                  name="password"
                  required
                  minLength={10}
                  placeholder={t("password.placeholder")}
                  label={t("password.label")}
                  size="2"
                  error={loginMutation.isError}
                />
                <Text
                  color="red"
                  size="2"
                  data-testid="password-error-message"
                  className={cx({ hidden: !loginMutation.isError })}
                >
                  {errorMessage}
                </Text>
              </Grid>
            ) : null}
            {showPassword ? (
              <Link to={forgotPasswordRoute.to} className="puntt-link">
                <Text color="blue" weight="medium">
                  {t("password.forgot")}
                </Text>
              </Link>
            ) : null}
            {showSso ? (
              <Button
                type="button"
                variant={showPassword ? "outline" : "solid"}
                className="w-full"
                data-testid="auth-dialog-sso-button"
                aria-label="Continue with SSO"
                disabled={ssoLoading}
                loading={ssoLoading}
                onClick={() => {
                  setSsoLoading(true);
                  ssoAuthorize({ email })
                    .then((policy) => {
                      if (policy?.redirectUrl) {
                        window.location.href = policy.redirectUrl;
                        return;
                      }
                      setErrorMessage(
                        "Unable to start SSO. Contact your admin.",
                      );
                    })
                    .catch((err: Error) => {
                      setErrorMessage(err.message);
                    })
                    .finally(() => setSsoLoading(false));
                }}
              >
                Continue with SSO
              </Button>
            ) : null}
            {showSso ? (
              // Reveal SSO failures. The SSO button sets `errorMessage`
              // (e.g. "Unable to start SSO…") WITHOUT running the login
              // mutation, so the password-field error above — gated on
              // `loginMutation.isError` — stays hidden, and on enforced SSO
              // (showPassword=false) that element isn't even rendered. Gate
              // this one on `errorMessage` so the user isn't left with a
              // cleared spinner and no feedback.
              <Text
                color="red"
                size="2"
                data-testid="sso-error-message"
                className={cx({ hidden: !errorMessage })}
              >
                {errorMessage}
              </Text>
            ) : null}
            {showPassword ? (
              <Button
                className="w-full"
                disabled={loginMutation.isPending}
                loading={loginMutation.isPending}
              >
                {t("buttons.login")}
              </Button>
            ) : null}
          </form>
        );
      }
      case AuthStep.INVITE_RESENT:
        return (
          <Flex direction="column" gap="4" className="mt-4">
            <Text data-testid="invite-resent-body">
              <Trans
                i18nKey="inviteResent.body"
                ns="auth"
                values={{ email }}
                components={{ strong: <strong /> }}
              />
            </Text>
            <Button
              className="mt-2 w-full"
              onClick={() => dispatch(setAuthenticationDialogOpen(false))}
              data-testid="invite-resent-close"
            >
              {t("authenticationDialog.got")}
            </Button>
          </Flex>
        );
      case AuthStep.MFA:
        return (
          <MfaForm
            mfaOptions={mfaOptions}
            loading={mfaMutation.isPending}
            onVerify={(otp) => {
              const startTime = performance.now();
              mfaMutation.mutate(
                {
                  authenticationChallengeId: mfaOptions?.authenticationChallenge
                    ?.id as string,
                  pendingAuthenticationToken:
                    mfaOptions?.pendingAuthenticationToken as string,
                  code: otp,
                },
                {
                  onSuccess: (data) => {
                    dispatch(
                      login({
                        token: (data as VerifyMfaResponse).token as string,
                      }),
                    );
                    posthog.capture("authentication_dialog_mfa_verification", {
                      requestDurationSeconds:
                        (performance.now() - startTime) / 1000,
                      email,
                      referredBy: user?.referredBy,
                    });
                    window.location.reload();
                  },
                  onError: (error) => {
                    setErrorMessage(error.message);
                    posthog.capture(
                      "authentication_dialog_mfa_verification_error",
                      {
                        requestDurationSeconds:
                          (performance.now() - startTime) / 1000,
                        email,
                        ...errorAnalyticsPayload(error),
                        referredBy: user?.referredBy,
                      },
                    );
                  },
                },
              );
            }}
            error={mfaMutation.isError}
            errorMessage={errorMessage}
            guestLogin
          />
        );
      default:
        return null;
    }
  };

  const handleModalClose = () => {
    setEmail("");
    setCurrentStep(AuthStep.EMAIL_VERIFICATION);
    setErrorMessage("");
    setMfaOptions({});
    // Drop the resolved SSO policy and any in-flight SSO spinner. Without
    // this, a first email that resolved to enforced SSO (loginPolicy =
    // { mode: "sso" }, password hidden) leaks into the NEXT session: the
    // dialog reopens, a different — password-based — user enters their
    // email, and the LOGIN step renders the SSO button with no password
    // field from the stale policy until (and only if) the fresh
    // ssoAuthorize resolves. Reset both so every session starts neutral.
    setLoginPolicy(null);
    setSsoLoading(false);
    loginMutation.reset();
    shareLinkViewAccessMutation.reset();
    mfaMutation.reset();
    verifyUserMutation.reset();
    // Drop a parked enterprise-switch step. Without this, a cancel would leave
    // it on the slice, and the next unrelated open (share-link wall, comment
    // login gate) would render the switch's TOTP step instead of the email
    // form.
    dispatch(clearEnterpriseMfaSwitch());
    if (isPasswordVisibilityToggled) togglePasswordVisibility();
  };

  useEffect(() => {
    if (!authenticationDialogOpen) {
      handleModalClose();
    }
  }, [authenticationDialogOpen]);

  // why: the close transition is not the only way out of a parked switch step.
  // A route change to a surface that mounts no dialog, or a logout without the
  // close transition, unmounts this component while `ui.enterpriseMfaSwitch`
  // still holds the pending token — and the next surface that opens the dialog
  // for an unrelated reason (share-link wall, comment login gate) would seed
  // its MFA step from that stale switch. `handleModalClose` cannot see this
  // path, so the unmount clears it.
  useEffect(
    () => () => {
      dispatch(clearEnterpriseMfaSwitch());
    },
    [dispatch],
  );

  // An enterprise switch into an MFA-required tenant parks the TOTP step here
  // (christo #6156) and opens the dialog; start on that step rather than the
  // email form. The verify below is the same one the password LOGIN path
  // uses, and its token is scoped to the enterprise being switched into, so
  // the existing MFA success path finishes the switch (dispatch `login` →
  // reload).
  //
  // why: keyed on the pending token, not on the step object. Every dispatch of
  // `openEnterpriseMfaSwitch` carries a fresh object — `mfaOptionsOf` builds
  // one — so an object-keyed effect re-runs on a re-park, and the
  // `setErrorMessage("")` below would blank the "invalid code" the user is
  // reading while a polling caller re-issues the switch.
  const appliedSwitchToken = useRef<string | null>(null);
  useEffect(() => {
    const pendingToken =
      enterpriseMfaSwitch?.pendingAuthenticationToken ?? null;
    // `enterpriseMfaSwitch` is in the guard as well as `pendingToken`: it is
    // the value passed to `setMfaOptions`, which takes no null.
    if (!authenticationDialogOpen || !enterpriseMfaSwitch || !pendingToken) {
      appliedSwitchToken.current = null;
      return;
    }

    if (appliedSwitchToken.current === pendingToken) return;
    appliedSwitchToken.current = pendingToken;

    setMfaOptions(enterpriseMfaSwitch);
    // A stale error from an earlier step would render under the MFA form.
    setErrorMessage("");
    setCurrentStep(AuthStep.MFA);
  }, [authenticationDialogOpen, enterpriseMfaSwitch]);

  return (
    <Dialog.Root
      open={authenticationDialogOpen}
      onOpenChange={(open) => dispatch(setAuthenticationDialogOpen(open))}
    >
      <Dialog.Content className="w-[400px]" onFocus={(e) => e.preventDefault()}>
        <VisuallyHidden>
          <Dialog.Title>
            {t("authenticationDialog.logCreateAccount")}
          </Dialog.Title>
        </VisuallyHidden>

        <VisuallyHidden>
          <Dialog.Description>
            Log in or create an account to view this shared ticket and its
            comments.
          </Dialog.Description>
        </VisuallyHidden>

        {/* No back button for an enterprise-switch MFA step: the dialog was
            not opened by the email flow, so "back" would land on a LOGIN form
            for an email the user never entered. Closing the dialog is the way
            out, and it leaves them on their current enterprise. */}
        {currentStep !== AuthStep.EMAIL_VERIFICATION &&
          currentStep !== AuthStep.INVITE_RESENT &&
          !enterpriseMfaSwitch && (
            <Flex className="mb-4">
              <ButtonNew
                variant="ghost"
                data-testid="auth-dialog-back-button"
                aria-label={t("common:actions.back")}
                onClick={() => {
                  if (currentStep === AuthStep.MFA) {
                    setMfaOptions({});
                    setCurrentStep(AuthStep.LOGIN);
                  } else if (currentStep == AuthStep.LOGIN) {
                    setCurrentStep(AuthStep.EMAIL_VERIFICATION);
                    setErrorMessage("");
                    // Clear the resolved SSO policy too, otherwise an
                    // enforced-SSO first email leaves the LOGIN form
                    // rendering the SSO button (password hidden) when the
                    // user comes back and submits a different, password
                    // account before the fresh ssoAuthorize resolves.
                    setLoginPolicy(null);
                    setSsoLoading(false);
                    loginMutation.reset();
                  }
                }}
              >
                <Icon.ArrowLeft />
                Back
              </ButtonNew>
            </Flex>
          )}
        <Flex align="center" direction="column" gap="4">
          <Logo />
          <Typography
            as="h3"
            weight="bold"
            size="2xl"
            data-testid="auth-dialog-heading"
          >
            {currentStep === AuthStep.MFA
              ? t("authenticationDialog.setUpAuthenticatorApp")
              : currentStep === AuthStep.EMAIL_VERIFICATION
                ? t("authenticationDialog.logCreateAccount")
                : currentStep === AuthStep.INVITE_RESENT
                  ? t("authenticationDialog.checkEmail")
                  : t("authenticationDialog.welcomeBack")}
          </Typography>
          {currentStep !== AuthStep.MFA &&
            currentStep !== AuthStep.INVITE_RESENT && (
              <Typography
                as="p"
                className="text-center"
                data-testid="auth-dialog-body"
              >
                {currentStep === AuthStep.EMAIL_VERIFICATION
                  ? t("authenticationDialog.enterEmailViewSharedTicket")
                  : t("authenticationDialog.logGetAccessAllAvailable")}
              </Typography>
            )}
        </Flex>

        {renderForm()}
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function useAuthenticationOverlay() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.value);
  const posthog = useAnalytics("useAuthenticationOverlay");

  useEffect(() => {
    const el = document.getElementById("root");

    if (el == null) return;

    const handleClick = (event: MouseEvent) => {
      if (!user) return;

      const target = event.target as HTMLElement | null;

      if (target == null) return;

      const triggerElem = target.closest("[data-auth-trigger]");

      if (triggerElem && user.referredBy != null) {
        event.preventDefault();
        event.stopImmediatePropagation();
        dispatch(setAuthenticationDialogOpen(true));
        posthog.capture("authentication_dialog_open", {
          referredBy: user?.referredBy,
          trigger: triggerElem.getAttribute("data-auth-trigger"),
        });
      }
    };

    const handleDragOver = (event: DragEvent) => {
      if (!user) return;

      if (user.referredBy != null) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleDrop = (event: DragEvent) => {
      if (!user) return;

      if (user.referredBy != null) {
        event.preventDefault();
        event.stopPropagation();
        dispatch(setAuthenticationDialogOpen(true));
        posthog.capture("authentication_dialog_open", {
          referredBy: user?.referredBy,
          trigger: "file_drop",
        });
      }
    };

    el.addEventListener("click", handleClick, { capture: true });
    el.addEventListener("dragover", handleDragOver);
    el.addEventListener("drop", handleDrop);

    return () => {
      el.removeEventListener("click", handleClick, { capture: true });
      el.removeEventListener("dragover", handleDragOver);
      el.removeEventListener("drop", handleDrop);
    };
  }, [dispatch, posthog, user]);

  return null;
}
